import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'

import { paths } from 'src/config/paths'
import ChatButton from 'src/features/chats/components/buttons/global-chat-button'
import { AiLightning } from 'src/shared/components/icons/ai-lightning'
import { UsersFilled } from 'src/shared/components/icons/feed/users-filled'
import { UsersOutline } from 'src/shared/components/icons/feed/users-outline'
import { Invite } from 'src/shared/components/icons/invite'
import { SubscriptionLogo } from 'src/shared/components/icons/logos/subscription'
import { MatchPeople } from 'src/shared/components/icons/match-people'
import { BellFilled } from 'src/shared/components/icons/notifications/bell-filled'
import { BellOutline } from 'src/shared/components/icons/notifications/bell-outline'
import { BriefcaseFilled } from 'src/shared/components/icons/referrals/briefcase-filled'
import { BriefcaseOutline } from 'src/shared/components/icons/referrals/briefcase-outline'
import { Button } from 'src/shared/components/ui/buttons/button'
import { SearchInput } from 'src/shared/components/ui/inputs/search-input'
import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import { ScrollArea } from 'src/shared/components/ui/miscellaneous/scroll-area'
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTrigger } from 'src/shared/components/ui/popups/sheet'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { UserCard } from 'src/shared/features/users/components/cards/user-card'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import { PercentSection } from 'src/shared/features/users/components/view-profile/percent-section'
import { VisibilityCard } from 'src/shared/features/visibility/components/card/scorebar'
import { cn } from 'src/shared/utils/cn'

import type { NavItem } from './nav-config'
import { mobileMenuItemsBottom, mobileMenuItemsTop } from './nav-config'

const NavMobile = ({
  hideHeader = false,
  hideFooter = false,
  onSearch,
}: {
  hideHeader?: boolean
  hideFooter?: boolean
  onSearch?: (query: string) => void
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const { data: currentUser } = useMyUserData()
  const location = useLocation()
  const navigate = useNavigate()

  const handleSearchChange = (value: string) => {
    onSearch?.(value)
    setIsSearchFocused(!!value)
  }

  const shouldShowItem = (item: NavItem) => {
    if (item.requiredRole) {
      return currentUser?.role === item.requiredRole
    }
    return true
  }

  const renderNavItem = (item: (typeof mobileMenuItemsTop)[0]) => {
    if (!shouldShowItem(item)) return null

    const isActive = location.pathname.includes(item.url.replace('/', ''))
    const Icon = isActive && item.activeIcon ? item.activeIcon : item.defaultIcon

    const content = (
      <div key={item.url} className={'flex flex-row items-center gap-3 text-lg font-medium'}>
        <Icon className="size-6" />
        {item.title}
        {item.comingSoon && <Badge text="SOON" variant="outline" className="ml-auto" />}
      </div>
    )

    return item.comingSoon ? (
      content
    ) : (
      <Link key={item.url} to={item.url} onClick={() => setIsSheetOpen(false)}>
        {content}
      </Link>
    )
  }

  const handleSendReferralClick = () => {
    setIsSheetOpen(false)
    navigate(paths.home.getHref())
  }

  const handleInviteClick = () => {
    setIsSheetOpen(false)
    navigate(paths.invite.getHref())
  }

  if (!currentUser) {
    return null
  }

  return (
    <>
      {!hideHeader && (
        <nav
          className="fixed left-0 top-0 z-50 h-mobile-header w-full bg-background"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mx-auto flex h-full items-center justify-between px-4">
            {!isSearchFocused && (
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <ProfileAvatar userId={currentUser.id} size={40} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="flex w-[300px] flex-col justify-between px-8 py-4 sm:w-[400px]">
                  <SheetHeader className="flex flex-col gap-3">
                    <SubscriptionLogo />

                    <div className="flex flex-col gap-5">{mobileMenuItemsTop.map((item) => renderNavItem(item))}</div>

                    <div className="flex flex-col gap-3">
                      <Button variant="default" className="rounded-3xl" onClick={handleSendReferralClick}>
                        Match / Refer
                      </Button>
                      <Button
                        variant="secondaryInverse"
                        className="flex w-full items-center justify-center gap-2 rounded-full"
                        onClick={handleInviteClick}
                      >
                        <Invite className="size-6" />
                        Invite
                      </Button>
                      <Button
                        variant="outline"
                        leftIcon={<AiLightning className="size-4" />}
                        className="flex w-full items-center justify-center rounded-full border-secondary"
                        onClick={() => navigate(paths.assistant.path)}
                      >
                        Chat with Lynk
                      </Button>
                    </div>
                  </SheetHeader>
                  <ScrollArea startAtBottom>
                    <SheetFooter className="flex w-full flex-col gap-4">
                      <div className="flex w-full flex-col gap-4">
                        {mobileMenuItemsBottom.filter(shouldShowItem).map((item) => renderNavItem(item))}
                        {currentUser && <PercentSection user={currentUser} variant="sidebar" />}
                        {currentUser && <VisibilityCard />}
                        {currentUser && (
                          <button
                            onClick={() => navigate(paths.users.profile.getHref(currentUser.username))}
                            className="w-full py-2 transition-opacity hover:opacity-80"
                          >
                            <UserCard
                              userId={currentUser.id}
                              buttonType="none"
                              className="w-full"
                              isMe={true}
                              showUsername={false}
                              showAddress={false}
                            />
                          </button>
                        )}
                      </div>
                    </SheetFooter>
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            )}

            <div
              className={cn(
                'relative mx-4 flex-1 transition-all duration-200',
                isSearchFocused ? 'flex-grow' : 'w-[180px]',
              )}
            >
              <SearchInput
                onChange={handleSearchChange}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="Search for referral partner"
                className="h-10 text-center text-sm"
                searchTerm=""
              />
            </div>

            {!isSearchFocused && <ChatButton />}
          </div>
        </nav>
      )}
      {!hideFooter && (
        <nav className="fixed bottom-0 left-0 z-50 w-full border-t bg-background pb-3">
          <div className="relative mx-auto flex h-full items-center justify-between px-4">
            <div className="flex w-2/5 justify-around">
              <Link to={paths.feed.path} className="flex flex-col items-center justify-center gap-1 text-foreground">
                {location.pathname.includes(paths.feed.path) ? (
                  <UsersFilled className="size-6" />
                ) : (
                  <UsersOutline className="size-6" />
                )}
                <span className={`text-[10px] ${location.pathname.includes(paths.feed.path) ? 'font-bold' : ''}`}>
                  Feed
                </span>
              </Link>
              <Link
                to={paths.referrals.list.received.getHref()}
                className="flex flex-col items-center justify-center gap-1 text-foreground"
              >
                {location.pathname.includes(paths.referrals.list.path) ? (
                  <BriefcaseFilled className="size-6" />
                ) : (
                  <BriefcaseOutline className="size-6" />
                )}
                <span
                  className={`text-[10px] ${location.pathname.includes(paths.referrals.list.path) ? 'font-bold' : ''}`}
                >
                  Business
                </span>
              </Link>
            </div>
            <div className="absolute -top-8 left-1/2 flex shrink-0 -translate-x-1/2 flex-col items-center gap-2">
              <Button
                size="icon"
                className="grid aspect-square size-14 shrink-0 place-items-center rounded-full bg-primary text-background shadow-lg shadow-secondary/30"
                onClick={() => navigate(paths.home.getHref())}
              >
                <MatchPeople className="size-6" />
              </Button>
              <span
                className={`translate-y-1 text-[10px] ${
                  location.pathname.includes(paths.home.path) ? 'font-bold' : ''
                }`}
              >
                Match / Refer
              </span>
            </div>
            <div className="flex w-2/5 justify-around">
              <Link
                to={paths.notifications.path}
                className="flex flex-col items-center justify-center gap-1 text-foreground"
              >
                {location.pathname.includes(paths.notifications.path) ? (
                  <BellFilled className="size-6" />
                ) : (
                  <BellOutline className="size-6" />
                )}
                <span
                  className={`text-[10px] ${location.pathname.includes(paths.notifications.path) ? 'font-bold' : ''}`}
                >
                  Notifications
                </span>
              </Link>
              <Link
                to={paths.assistant.path}
                className="flex flex-col items-center justify-center gap-1 rounded-lg p-2"
              >
                <AiLightning className="size-6" />
                <span className={`text-[10px] ${location.pathname.includes(paths.assistant.path) ? 'font-bold' : ''}`}>
                  Lynk
                </span>
              </Link>
            </div>
          </div>
        </nav>
      )}
    </>
  )
}

export default NavMobile
